import React from 'react';
import { ExpenseConfigForm } from '@/components/config_forms/expense/ExpenseConfigForm';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import type { ValidationError } from '@/utils/validation/types';
import { getFieldErrorElement } from '@/utils/validation/helpers';
import type { ExpenseConfig } from '@/types/ExpenseConfig';
import type { ExpenseCategory } from '@/types/ExpenseCategory';

interface ClientExpenseConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: unknown) => void;
  isCustomized?: boolean;
  onRevert?: () => void;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
}

export const ClientExpenseConfig: React.FC<ClientExpenseConfigProps> = ({
  formData,
  handleChange,
  isCustomized,
  onRevert,
  errors = [],
  touchedFields = new Set(),
}) => {
  const config: ExpenseConfig = {
    expenseCategories: formData.expense_categories as ExpenseCategory[],
    expensePolicies: formData.expense_policies,
    expenseMileageRate: formData.expense_mileage_rate,
    expenseMileageUnit: formData.expense_mileage_unit as 'miles' | 'km',
    defaultExpenseMarkupType: formData.default_expense_markup_type,
    defaultExpenseMarkupPercentage: formData.default_expense_markup_percentage,
    defaultExpenseMarkupFixedAmount: formData.default_expense_markup_fixed_amount,
    expenseReceiptRequiredAboveAmount: formData.expense_receipt_required_above_amount,
  };

  const categories = (formData.expense_categories as ExpenseCategory[]) ?? [];

  const handleCategoriesChange = (updated: ExpenseCategory[]) => {
    handleChange('expense_categories', updated);
  };

  const onConfigChange = (updates: Partial<ExpenseConfig>) => {
    Object.entries(updates).forEach(([key, value]) => {
      const fieldKey = key === 'expensePolicies' ? 'expense_policies' :
                      key === 'expenseMileageRate' ? 'expense_mileage_rate' :
                      key === 'expenseMileageUnit' ? 'expense_mileage_unit' :
                      key === 'defaultExpenseMarkupType' ? 'default_expense_markup_type' :
                      key === 'defaultExpenseMarkupPercentage' ? 'default_expense_markup_percentage' :
                      key === 'defaultExpenseMarkupFixedAmount' ? 'default_expense_markup_fixed_amount' :
                      key === 'expenseReceiptRequiredAboveAmount' ? 'expense_receipt_required_above_amount' :
                      key;
      handleChange(fieldKey as keyof ClientFormData, value);
    });
  };

  return (
    <ExpenseConfigForm
      title="Expense Policies"
      description="Override default expense settings for this client."
      expenseCategories={categories}
      onCategoriesChange={handleCategoriesChange}
      config={config}
      onConfigChange={onConfigChange}
      mileageRateProps={{
        value: formData.expense_mileage_rate ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('expense_mileage_rate', e.target.value ? parseFloat(e.target.value) : null),
      }}
      mileageRateError={getFieldErrorElement('expense_mileage_rate', errors, touchedFields)}
      receiptRequirementProps={{
        value: formData.expense_receipt_required_above_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('expense_receipt_required_above_amount', e.target.value ? parseFloat(e.target.value) : null),
      }}
      receiptRequirementError={getFieldErrorElement('expense_receipt_required_above_amount', errors, touchedFields)}
      markupPercentageProps={{
        value: formData.default_expense_markup_percentage ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('default_expense_markup_percentage', e.target.value ? parseFloat(e.target.value) : null),
      }}
      markupPercentageError={getFieldErrorElement('default_expense_markup_percentage', errors, touchedFields)}
      markupFixedAmountProps={{
        value: formData.default_expense_markup_fixed_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('default_expense_markup_fixed_amount', e.target.value ? parseFloat(e.target.value) : null),
      }}
      markupFixedAmountError={getFieldErrorElement('default_expense_markup_fixed_amount', errors, touchedFields)}
      isCustomized={isCustomized}
      onRevert={onRevert}
      context="client"
    />
  );
};
